import { FormEvent } from "react";
import { FormikErrors } from "formik";
import { Button, Image, Input } from "@atoms";
import { AuthSection } from "@organisms";
import { useTranslation } from "react-i18next";

export interface FormValues {
  code: string;
  password: string;
  confirmPassword: string;
}

export type ResetPasswordPageTemplateProps = {
  values: FormValues;
  errors: FormikErrors<FormValues>;
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleSubmit: (e?: FormEvent<HTMLFormElement> | undefined) => void;
  backToSignIn: () => void;
  loading: boolean;
  success: boolean;
};

export const ResetPasswordPageTemplate: React.FC<ResetPasswordPageTemplateProps> = ({
  values,
  errors,
  handleInputChange,
  handleSubmit,
  backToSignIn,
  loading,
  success
}: ResetPasswordPageTemplateProps): JSX.Element => {
  const { t } = useTranslation();
  const { code, password, confirmPassword } = values;
  const {
    code: codeError,
    password: passwordError,
    confirmPassword: confirmPasswordError,
  } = errors;

  return (
    <div className="min-h-screen flex">
      <AuthSection />

      {/* Right side - Form */}
      <div className="w-full lg:w-1/2 min-h-screen flex items-center justify-center bg-white">
        <div className="w-full max-w-md px-8">
          {/* Mobile logo */}
          <div className="lg:hidden absolute top-8 left-8">
            <div className="flex items-center gap-2">
              <Image variant="parachuteLogo" height={32} width={32} />
              <span className="text-neutral-darkest text-xl font-medium">Parachute</span>
            </div>
          </div>

          <form
            className="text-center flex flex-col items-center max-w-xs mx-auto"
            onSubmit={handleSubmit}
          >
            {!success ? (
              <div>
                <div className="px-3">
                  <div className="mb-2">
                    <h3 className="text-3xl font-black text-neutral-darkest">{t("auth.resetPassword.title")}</h3>
                  </div>
                </div>
                <div className="px-3 mb-6">
                  <div className="">
                    <h3 className="text-md text-gray-600">
                      {t("auth.resetPassword.subtitle")}
                    </h3>
                  </div>
                </div>
                <div className="w-full">
                  <div className="mb-2">
                    <Input
                      name="code"
                      variant="text"
                      onChange={handleInputChange}
                      placeholder="Code"
                      value={code}
                      errorMessage={codeError}
                    />
                  </div>
                </div>
                <div className="w-full">
                  <div className="mb-2">
                    <Input
                      name="password"
                      variant="password"
                      onChange={handleInputChange}
                      placeholder={t("auth.resetPassword.newPassword")}
                      value={password}
                      errorMessage={passwordError}
                    />
                  </div>
                </div>
                <div className="w-full">
                  <div className="mb-2">
                    <Input
                      name="confirmPassword"
                      variant="password"
                      onChange={handleInputChange}
                      placeholder={t("auth.resetPassword.confirmPassword")}
                      value={confirmPassword}
                      errorMessage={confirmPasswordError}
                    />
                  </div>
                </div>
                <div className="mt-8 mb-6 w-full sm:mb-4">
                  <div>
                    <Button
                      type="submit"
                      variant="primary"
                      label={t("auth.resetPassword.submit")}
                      stretch
                      color="primary-medium"
                      hoverColor="primary-dark"
                      textColor="white"
                      loading={loading}
                    />
                  </div>
                </div>
              </div>
            ) : (
              <div>
                <div>
                  <h3 className="text-3xl font-black text-neutral-darkest">
                    {t("auth.resetPassword.successTitle")}
                  </h3>
                </div>
                <div className="mt-4">
                  <h3 className="text-md text-gray-600">
                    {t("auth.resetPassword.successDescription")}
                  </h3>
                </div>
                <div className="flex flex-col justify-center items-center"> 
                  <div className="mt-8 w-2/3">
                    <Button
                      type="submit"
                      variant="primary"
                      label={t("auth.resetPassword.backToSignIn")}
                      stretch
                      color="primary-medium"
                      hoverColor="primary-dark"
                      textColor="white"
                      onClick={backToSignIn}
                    />
                  </div>
                </div>
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};
